/**
 * Player dos depoimentos em áudio (<e-audio-player>).
 *
 * Sem JS fica o <audio controls> nativo. Com JS: botão ouvir/pausar, tempo e
 * barra de posição (input range, acessível por teclado). Só um depoimento
 * toca por vez. O arquivo não é baixado antes de o player chegar perto da
 * tela; aí o navegador busca só os metadados para mostrar a duração.
 */

const players = new Set();

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

class EAudioPlayer extends HTMLElement {
  connectedCallback() {
    this.audio = this.querySelector('audio');
    this.toggleButton = this.querySelector('[data-audio-toggle]');
    this.range = this.querySelector('[data-audio-range]');
    this.label = this.querySelector('[data-audio-label]');
    this.current = this.querySelector('[data-audio-current]');
    this.total = this.querySelector('[data-audio-total]');
    if (!this.audio || !this.toggleButton || !this.range) return;

    this.name = this.dataset.name ?? '';
    this.audio.controls = false;
    this.setAttribute('data-enhanced', '');
    players.add(this);

    this.toggleButton.addEventListener('click', () => this.toggle());
    this.range.addEventListener('input', () => this.seek());
    this.audio.addEventListener('loadedmetadata', () => this.renderDuration());
    this.audio.addEventListener('durationchange', () => this.renderDuration());
    this.audio.addEventListener('timeupdate', () => this.renderProgress());
    this.audio.addEventListener('play', () => this.renderState(true));
    this.audio.addEventListener('pause', () => this.renderState(false));
    this.audio.addEventListener('ended', () => {
      this.audio.currentTime = 0;
      this.renderProgress();
    });
    this.observeViewport();
  }

  disconnectedCallback() {
    players.delete(this);
    this.observer?.disconnect();
  }

  observeViewport() {
    if (!('IntersectionObserver' in window)) {
      this.loadMetadata();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.loadMetadata();
      },
      { rootMargin: '400px 0px' },
    );
    this.observer.observe(this);
  }

  loadMetadata() {
    if (this.audio.preload !== 'none') return;
    this.audio.preload = 'metadata';
    this.audio.load();
  }

  toggle() {
    if (!this.audio.paused) {
      this.audio.pause();
      return;
    }
    players.forEach((player) => {
      if (player !== this) player.audio.pause();
    });
    this.audio.play().catch(() => this.renderState(false));
  }

  seek() {
    if (!Number.isFinite(this.audio.duration)) return;
    this.audio.currentTime = Number(this.range.value);
    this.renderProgress();
  }

  renderDuration() {
    const duration = this.audio.duration;
    if (!Number.isFinite(duration)) return;
    this.range.max = String(duration);
    if (this.total) this.total.textContent = formatTime(duration);
    this.renderProgress();
  }

  renderProgress() {
    const duration = this.audio.duration;
    const position = this.audio.currentTime;
    const percent = Number.isFinite(duration) && duration > 0 ? (position / duration) * 100 : 0;
    this.range.value = String(position);
    // No player inteiro: a linha do range e a forma de onda leem o mesmo valor.
    this.style.setProperty('--progress', `${percent}%`);
    this.range.setAttribute('aria-valuetext', `${formatTime(position)} de ${formatTime(duration)}`);
    if (this.current) this.current.textContent = formatTime(position);
  }

  renderState(playing) {
    this.toggleAttribute('data-playing', playing);
    if (this.label) {
      this.label.textContent = `${playing ? 'Pausar' : 'Ouvir'} o depoimento de ${this.name}`;
    }
  }
}

if (!customElements.get('e-audio-player')) customElements.define('e-audio-player', EAudioPlayer);
